import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Sparkles,
  CalendarDays,
  Send,
  Rocket,
  Activity as ActivityIcon,
  Image as ImageIcon,
  ChevronRight,
  Wand2,
  Building2,
  FileText,
  ClipboardCheck,
  ArrowRight,
  ArrowUpRight,
  ExternalLink,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { StatusBadge } from "@/components/Surface";
import GlassCard from "@/components/GlassCard";
import StatCard from "@/components/StatCard";
import WeeklyChart from "@/components/WeeklyChart";
import ActivityFeed from "@/components/ActivityFeed";
import EngagementWidget from "@/components/EngagementWidget";
import GradientMesh from "@/components/GradientMesh";
import PlanUsageWidget from "@/components/PlanUsageWidget";
import BrandLearningStatusCard from "@/components/BrandLearningStatusCard";
import AIStatusOrb from "@/components/AIStatusOrb";
import OnboardingChecklist, {
  defaultOnboardingItems,
  isChecklistComplete,
} from "@/components/OnboardingChecklist";
import ConnectionsHealthStrip from "@/components/ConnectionsHealthStrip";
import DashboardAlerts from "@/components/DashboardAlerts";
import { useAuth } from "@/lib/auth";

function buildSpark(weeks, key) {
  return (weeks || []).map((w) => w[key] || 0);
}

function relTime(iso) {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    const diff = Date.now() - d.getTime();
    const abs = Math.abs(diff);
    const min = 60 * 1000;
    const hr = 60 * min;
    const day = 24 * hr;
    const wk = 7 * day;
    if (abs < min) return "just now";
    if (abs < hr) return `${Math.round(abs / min)}m ${diff >= 0 ? "ago" : "from now"}`;
    if (abs < day) return `${Math.round(abs / hr)}h ${diff >= 0 ? "ago" : "from now"}`;
    if (abs < wk) return `${Math.round(abs / day)}d ${diff >= 0 ? "ago" : "from now"}`;
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  } catch {
    return null;
  }
}

export default function DashboardPage() {
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [posts, setPosts] = useState([]);
  const [stats, setStats] = useState(null);
  const [weekly, setWeekly] = useState([]);
  const [activity, setActivity] = useState([]);
  const [engagement, setEngagement] = useState({ items: [], totals: {} });
  const [autopilot, setAutopilot] = useState(null);
  const [connectionsAll, setConnectionsAll] = useState(null);
  const [pendingCount, setPendingCount] = useState(0);

  // ---- Onboarding checklist dismiss (per-user localStorage) --------
  const dismissKey = user?.user_id
    ? `postmoro.dashboard.onboardingChecklist.dismissed.${user.user_id}`
    : "postmoro.dashboard.onboardingChecklist.dismissed.anon";
  const [checklistDismissed, setChecklistDismissed] = useState(() => {
    try {
      return typeof window !== "undefined" && window.localStorage
        ? window.localStorage.getItem(dismissKey) === "1"
        : false;
    } catch {
      return false;
    }
  });

  // ---- Brand Learning dismiss (per-user localStorage) --------------
  const brandDismissKey = user?.user_id
    ? `postmoro.dashboard.brandLearning.dismissed.${user.user_id}`
    : "postmoro.dashboard.brandLearning.dismissed.anon";
  const [brandLearningDismissed, setBrandLearningDismissed] = useState(() => {
    try {
      return typeof window !== "undefined" && window.localStorage
        ? window.localStorage.getItem(brandDismissKey) === "1"
        : false;
    } catch {
      return false;
    }
  });

  // ---- Data load --------------------------------------------------
  // NOTE: /connections/instagram/setup-status was previously fetched here
  // but its output was never rendered — the aggregated /connections
  // payload provides everything the Dashboard needs. Removed to save an
  // extra request per Dashboard load. (Backend endpoint itself untouched.)
  const load = async () => {
    const endpoints = [
      ["profile", "/profile"],
      ["posts", "/posts"],
      ["stats", "/stats/overview"],
      ["weekly", "/stats/weekly"],
      ["activity", "/stats/activity"],
      ["engagement", "/stats/engagement"],
      ["autopilot", "/autopilot/settings"],
      ["connections_all", "/connections"],
      ["pending", "/posts/pending-approval"],
    ];
    const results = await Promise.allSettled(
      endpoints.map(([, path]) => api.get(path)),
    );
    const out = {};
    const failures = [];
    results.forEach((r, i) => {
      const [key, path] = endpoints[i];
      if (r.status === "fulfilled") {
        out[key] = r.value?.data;
      } else {
        failures.push({ key, path, status: r.reason?.response?.status });
      }
    });

    if ("profile" in out) setProfile(out.profile);
    if ("posts" in out) setPosts(out.posts || []);
    if ("stats" in out) setStats(out.stats);
    if ("weekly" in out) setWeekly(out.weekly?.weeks || []);
    if ("activity" in out) setActivity(out.activity?.events || []);
    if ("engagement" in out) setEngagement(out.engagement || { items: [], totals: {} });
    if ("autopilot" in out) setAutopilot(out.autopilot);
    if ("connections_all" in out) setConnectionsAll(out.connections_all);
    if ("pending" in out) setPendingCount((out.pending || []).length);

    if (failures.length > 0) {
      console.warn("[postmoro] dashboard partial load", failures);
      if (failures.length === endpoints.length) {
        toast.error("Failed to load dashboard");
      }
    }
  };

  useEffect(() => {
    load();
  }, []);

  // ---- Derived state ----------------------------------------------
  const autopilotState =
    autopilot?.mode === "autopilot" && autopilot?.enabled && !autopilot?.paused
      ? "running"
      : autopilot?.paused
        ? "paused"
        : "idle";
  const autopilotEnabled = !!autopilot?.enabled && autopilot?.mode === "autopilot";

  const hasAnyRealConnection = useMemo(() => {
    const plats = connectionsAll?.platforms || [];
    return plats.some((p) => p.connected && !p.needs_reauth);
  }, [connectionsAll]);

  const upcoming = useMemo(
    () =>
      [...(posts || [])]
        .filter((p) => p.status === "scheduled" || p.status === "pending_approval")
        .sort((a, b) => new Date(a.scheduled_at || a.created_at) - new Date(b.scheduled_at || b.created_at))
        .slice(0, 4),
    [posts],
  );

  const recentlyPublished = useMemo(
    () =>
      [...(posts || [])]
        .filter((p) => p.status === "published" && p.published_at)
        .sort((a, b) => new Date(b.published_at) - new Date(a.published_at))
        .slice(0, 3),
    [posts],
  );

  const totalPostsForWeeklyChart = useMemo(
    () => weekly.reduce((s, w) => s + (w.published || 0) + (w.scheduled || 0) + (w.drafts || 0), 0),
    [weekly],
  );

  // ---- Onboarding checklist ---------------------------------------
  const checklistItems = useMemo(
    () =>
      defaultOnboardingItems({
        profile,
        posts,
        connectionsPayload: connectionsAll,
      }),
    [profile, posts, connectionsAll],
  );
  const checklistAllDone = useMemo(
    () => isChecklistComplete(checklistItems),
    [checklistItems],
  );
  const showChecklist = !checklistDismissed && !checklistAllDone;

  const handleDismissChecklist = () => {
    setChecklistDismissed(true);
    try {
      if (typeof window !== "undefined" && window.localStorage) {
        window.localStorage.setItem(dismissKey, "1");
      }
    } catch {
      /* localStorage disabled — in-memory dismiss for this session */
    }
  };

  const handleDismissBrandLearning = () => {
    setBrandLearningDismissed(true);
    try {
      if (typeof window !== "undefined" && window.localStorage) {
        window.localStorage.setItem(brandDismissKey, "1");
      }
    } catch {
      /* fallback: in-memory dismiss */
    }
  };

  const hasZeroPosts = (posts?.length || 0) === 0;
  const showFirstRunHero = hasZeroPosts;

  // ---- Autopilot subtitle (context-aware, non-scolding) -----------
  // Uses existing state to explain WHY Autopilot is idle so a manual
  // user isn't nagged. No new backend fields.
  const autopilotSubtitle = (() => {
    if (autopilotState === "running") {
      return autopilot?.next_run_at
        ? `Next run ${relTime(autopilot.next_run_at) || "soon"}`
        : "Working in the background.";
    }
    if (autopilotState === "paused") {
      return "Autopilot is paused. Resume whenever you're ready.";
    }
    // idle
    if (!hasAnyRealConnection) {
      return "Connect a social account to get Autopilot running.";
    }
    return "Autopilot is off. Turn it on whenever you want Postmoro to handle posting automatically.";
  })();

  // ---- Coming Up empty-state (Autopilot-aware) --------------------
  const comingUpEmptyState = (() => {
    if (autopilotEnabled) {
      const nextRun = autopilot?.next_run_at
        ? new Date(autopilot.next_run_at).toLocaleString()
        : null;
      return (
        <div className="text-sm text-[color:var(--text-2)]" data-testid="dashboard-upcoming-empty">
          <span className="inline-flex items-center gap-1.5 text-[color:var(--brand-primary)] font-medium">
            <Rocket className="h-3.5 w-3.5" /> Autopilot is active.
          </span>{" "}
          {nextRun ? (
            <>Postmoro will queue the next post around <span className="text-[color:var(--text-1)]">{nextRun}</span>.</>
          ) : (
            "Postmoro will queue the next post automatically."
          )}
        </div>
      );
    }
    return (
      <div className="text-sm text-[color:var(--text-3)]" data-testid="dashboard-upcoming-empty">
        Nothing scheduled yet.{" "}
        <Link to="/generate" className="text-[color:var(--brand-primary)] hover:underline">
          Generate a post
        </Link>
        .
      </div>
    );
  })();

  // ---- Whether to hide "Best week" (needs meaningful history) -----
  // Rule: show only when there are at least 6 posts total AND we have
  // data spanning at least 3 weeks. Below that it's a meaningless label.
  const weeksWithActivity = weekly.filter(
    (w) => (w.published || 0) + (w.scheduled || 0) + (w.drafts || 0) > 0,
  ).length;
  const showBestWeek =
    totalPostsForWeeklyChart >= 6 && weeksWithActivity >= 3;

  // ---- Brand Learning card visibility -----------------------------
  const showBrandLearning = !brandLearningDismissed;

  // ---- Business profile summary -----------------------------------
  const profileSummary = useMemo(() => {
    if (!profile) return null;
    const parts = [
      profile.business_name,
      profile.business_type,
      profile.location,
      profile.tone ? `${profile.tone} tone` : null,
    ].filter(Boolean);
    return parts.join(" · ");
  }, [profile]);
  const profileComplete = !!(profile && profile.business_name && profile.business_type);

  // ---- Render ------------------------------------------------------
  return (
    <div className="relative">
      <GradientMesh />
      <div className="relative">
        {/* ---- 1. COMMAND CENTER HERO ------------------------------
           Replaces the previous plain PageHeader row + separate
           Autopilot strip. One premium panel that answers three
           questions at a glance: who am I, what is Postmoro doing
           for me, and where is my content right now. All fields
           come from the same existing data — no new fetches. */}
        {(() => {
          const rawFirst = (user?.first_name || "").trim();
          const rawName = (user?.name || "").trim();
          const rawBiz = (profile?.business_name || "").trim();
          const displayName =
            rawFirst
            || (rawName && rawName.toLowerCase() !== (user?.email || "").split("@")[0].toLowerCase() ? rawName.split(" ")[0] : "")
            || rawBiz
            || "";
          const greetTitle = displayName ? `Welcome back, ${displayName}` : "Your Postmoro overview";
          const greetSub = profile?.business_name
            ? `Your AI marketing assistant ${pendingCount > 0 ? `has ${pendingCount} idea${pendingCount === 1 ? "" : "s"} waiting and ` : ""}is working on ${profile.business_name}.`
            : "Your AI marketing assistant is ready to write, schedule, and publish on your behalf.";
          const apLive = autopilotState === "running";
          const apPaused = autopilotState === "paused";
          const apDotClass = apLive ? "" : apPaused ? "warn" : "idle";
          const apStateLabel = apLive ? "Live" : apPaused ? "Paused" : "Off";
          const apHeadline = apLive ? "Autopilot is running" : apPaused ? "Autopilot is paused" : "Autopilot is off";
          return (
            <div
              className="relative rounded-[20px] glass overflow-hidden mb-4"
              data-testid="dashboard-command-center"
            >
              {/* Corner glow */}
              <div
                aria-hidden="true"
                className="pointer-events-none absolute -top-24 -right-24 h-64 w-64 rounded-full"
                style={{ background: "radial-gradient(closest-side, rgba(27,168,159,0.24), transparent 70%)" }}
              />
              <div
                aria-hidden="true"
                className="pointer-events-none absolute -bottom-24 -left-16 h-56 w-56 rounded-full"
                style={{ background: "radial-gradient(closest-side, rgba(34,211,238,0.14), transparent 70%)" }}
              />
              <div className="relative p-5 sm:p-6 lg:p-7 flex flex-col lg:flex-row lg:items-stretch gap-5 lg:gap-6">
                {/* LEFT — Greeting + Autopilot status */}
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] uppercase tracking-[0.16em] text-[color:var(--brand-primary)]">Postmoro · command center</div>
                  <h1
                    className="font-display text-3xl sm:text-4xl font-semibold text-[color:var(--text-1)] tracking-tight mt-2"
                    data-testid="dashboard-title"
                  >
                    {greetTitle}
                  </h1>
                  <p className="text-sm sm:text-base text-[color:var(--text-2)] mt-1.5 max-w-2xl" data-testid="dashboard-subtitle">
                    {greetSub}
                  </p>
                  <div className="mt-4 flex flex-wrap gap-2">
                    <Link to="/autopilot">
                      <Button variant="outline" className="border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] text-[color:var(--text-1)]" data-testid="dashboard-autopilot-shortcut">
                        <Rocket className="h-4 w-4 mr-1.5" /> Autopilot
                      </Button>
                    </Link>
                    <Link to="/generate">
                      <Button className="bg-[color:var(--brand-primary)] hover:bg-[color:var(--brand-primary-hover)] text-[color:var(--bg-0)] font-semibold" data-testid="dashboard-quick-generate">
                        <Sparkles className="h-4 w-4 mr-1.5" /> Generate a post
                      </Button>
                    </Link>
                  </div>

                  {/* Autopilot row — inline card that replaces the standalone strip */}
                  <div
                    className="mt-5 rounded-[14px] border border-[color:var(--border-subtle)] bg-[color:rgba(255,255,255,0.02)] p-4 flex items-center gap-4 flex-wrap"
                    data-testid="dashboard-autopilot-strip"
                  >
                    <AIStatusOrb state={autopilotState} size={56} />
                    <div className="flex-1 min-w-[200px]">
                      <div className="text-[10px] uppercase tracking-[0.14em] text-[color:var(--text-3)]">AI Employee</div>
                      <div className="font-display text-lg font-semibold text-[color:var(--text-1)] mt-0.5">{apHeadline}</div>
                      <div className="text-xs text-[color:var(--text-3)] mt-1 flex items-center gap-3 flex-wrap">
                        <span className="inline-flex items-center gap-1.5">
                          <span className={"pulse-dot " + apDotClass} />
                          {apStateLabel}
                        </span>
                        <span data-testid="dashboard-autopilot-subtitle">{autopilotSubtitle}</span>
                        {autopilot?.last_run_at ? (
                          <span className="text-[color:var(--text-3)]">Last run: {relTime(autopilot.last_run_at)}</span>
                        ) : null}
                      </div>
                    </div>
                    <Link to="/autopilot">
                      <Button variant="outline" size="sm" className="border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] text-[color:var(--text-1)]" data-testid="dashboard-autopilot-open">
                        Open autopilot <ArrowUpRight className="h-4 w-4 ml-1" />
                      </Button>
                    </Link>
                  </div>
                </div>

                {/* RIGHT — At-a-glance stats (real fields only) */}
                <div className="lg:w-[300px] shrink-0">
                  <div className="text-[10px] uppercase tracking-[0.16em] text-[color:var(--text-3)] mb-3">Right now</div>
                  <div className="grid grid-cols-2 gap-2.5">
                    {[
                      { label: "Published", value: stats?.published ?? 0, color: "#1BA89F", icon: Send },
                      { label: "Scheduled", value: stats?.scheduled ?? 0, color: "#22D3EE", icon: CalendarDays },
                      { label: "Drafts",    value: stats?.drafts    ?? 0, color: "#2F8FE5", icon: FileText },
                      { label: "Pending",   value: stats?.pending_approval ?? 0, color: "#FFBE6E", icon: ClipboardCheck },
                    ].map((s) => (
                      <div
                        key={s.label}
                        className="relative rounded-[12px] border border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] p-3 overflow-hidden"
                      >
                        <span
                          aria-hidden="true"
                          className="absolute inset-x-0 top-0 h-[2px]"
                          style={{ background: s.color, opacity: 0.75 }}
                        />
                        <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-[color:var(--text-3)]">
                          <s.icon className="h-3 w-3" style={{ color: s.color }} />
                          {s.label}
                        </div>
                        <div className="font-display text-2xl font-semibold text-[color:var(--text-1)] mt-1 tabular-nums">
                          {s.value}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          );
        })()}

        {/* 2. Critical alerts (reauth / payment) — page level */}
        <DashboardAlerts connectionsPayload={connectionsAll} />

        {/* Pending-approval banner (existing behavior preserved) */}
        {pendingCount > 0 ? (
          <motion.div
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-4 rounded-[16px] glass p-4 flex items-center justify-between flex-wrap gap-3 border border-[color:rgba(255,190,110,0.35)]"
            data-testid="pending-approval-banner"
          >
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-md bg-[color:rgba(255,190,110,0.14)] flex items-center justify-center text-[color:rgb(255,190,110)]">
                <Sparkles className="h-4 w-4" />
              </div>
              <div>
                <div className="font-display text-base font-semibold text-[color:rgb(255,190,110)]">
                  {pendingCount} post{pendingCount === 1 ? "" : "s"} waiting for approval
                </div>
                <div className="text-xs text-[color:var(--text-2)]">Autopilot drafted new content. Review and approve to queue them.</div>
              </div>
            </div>
            <Link to="/autopilot">
              <Button className="bg-[color:rgb(255,190,110)] hover:opacity-90 text-[color:var(--bg-0)] font-semibold" data-testid="pending-approval-cta">
                Review now <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </Link>
          </motion.div>
        ) : null}

        {/* 3. Onboarding elements — ONLY when applicable */}
        {showChecklist ? (
          <div className="mb-4">
            <OnboardingChecklist
              items={checklistItems}
              onDismiss={handleDismissChecklist}
            />
          </div>
        ) : null}

        {showFirstRunHero ? (
          <div
            className="mb-4 rounded-[20px] glass p-6 sm:p-8 relative overflow-hidden"
            data-testid="dashboard-first-run-hero"
          >
            <div className="flex flex-col sm:flex-row sm:items-center gap-5 sm:gap-6">
              <div className="h-14 w-14 rounded-2xl bg-[color:rgba(27,168,159,0.15)] text-[color:var(--brand-primary)] flex items-center justify-center shrink-0">
                <Wand2 className="h-6 w-6" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="eyebrow">Get started</div>
                <div className="font-display text-2xl sm:text-3xl font-semibold text-[color:var(--text-1)] mt-1">
                  Ready to create your first post?
                </div>
                <div className="text-sm sm:text-base text-[color:var(--text-2)] mt-1.5 max-w-xl">
                  Postmoro can create the caption, hashtags, and content for you.
                  Generate one yourself or let Autopilot handle it.
                </div>
                <div className="mt-4 flex flex-wrap gap-2">
                  <Link to="/generate">
                    <Button
                      className="bg-[color:var(--brand-primary)] hover:bg-[color:var(--brand-primary-hover)] text-[color:var(--bg-0)] font-semibold"
                      data-testid="dashboard-first-run-generate"
                    >
                      <Wand2 className="h-4 w-4 mr-1" /> Generate a post
                    </Button>
                  </Link>
                  <Link to="/autopilot">
                    <Button
                      variant="outline"
                      className="border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] text-[color:var(--text-1)]"
                      data-testid="dashboard-first-run-autopilot"
                    >
                      <Rocket className="h-4 w-4 mr-1" /> Try Autopilot
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        ) : null}

        {/* 4. Autopilot status — now rendered inline inside the
           Command Center hero above. Kept the outer marker so any
           anchor references still resolve. */}

        {/* 5. Social connection health */}
        <div className="mb-4">
          <ConnectionsHealthStrip payload={connectionsAll} />
        </div>

        {/* 6. Core posting stats — owner-useful only. Removed lifetime
             "AI generated" vanity metric. */}
        {!hasZeroPosts ? (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
            <StatCard
              label="Published"
              value={stats?.published ?? 0}
              hint="Live posts"
              icon={Send}
              color="#1BA89F"
              spark={buildSpark(weekly, "published")}
              delay={0.0}
              testid="stat-published"
            />
            <StatCard
              label="Scheduled"
              value={stats?.scheduled ?? 0}
              hint="In the queue"
              icon={CalendarDays}
              color="#22D3EE"
              spark={buildSpark(weekly, "scheduled")}
              delay={0.05}
              testid="stat-scheduled"
            />
            <StatCard
              label="Drafts"
              value={stats?.drafts ?? 0}
              hint="Ready to review"
              icon={FileText}
              color="#2F8FE5"
              spark={buildSpark(weekly, "drafts")}
              delay={0.1}
              testid="stat-drafts"
            />
            <StatCard
              label="Pending approval"
              value={stats?.pending_approval ?? 0}
              hint="Autopilot drafts"
              icon={ClipboardCheck}
              color="#FFBE6E"
              delay={0.15}
              testid="stat-pending"
            />
          </div>
        ) : null}

        {/* 7. Coming up + 8. Recently published (side by side on wide screens) */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
          <GlassCard className="p-5" delay={0.16} data-testid="dashboard-upcoming-card">
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="eyebrow">Up next</div>
                <div className="font-display text-xl font-semibold">Coming up</div>
              </div>
              <Link to="/calendar" className="text-xs text-[color:var(--brand-primary)] hover:underline inline-flex items-center gap-1">
                Open calendar <ChevronRight className="h-3 w-3" />
              </Link>
            </div>
            {upcoming.length === 0 ? (
              comingUpEmptyState
            ) : (
              <ul className="space-y-2">
                {upcoming.map((p) => (
                  <motion.li
                    key={p.post_id}
                    initial={{ opacity: 0, y: 4 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="flex items-start gap-3 rounded-md border border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] p-2.5"
                    data-testid="dashboard-upcoming-row"
                  >
                    {p.image_url ? (
                      <img src={p.image_url} alt="" className="h-12 w-12 rounded-md object-cover border border-[color:var(--border-subtle)] shrink-0" />
                    ) : (
                      <div className="h-12 w-12 rounded-md bg-[color:var(--surface-3)] flex items-center justify-center text-[color:var(--brand-primary)] shrink-0">
                        <ImageIcon className="h-4 w-4" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-[color:var(--text-1)] line-clamp-2 whitespace-pre-line">{p.caption}</div>
                      <div className="mt-1 flex items-center gap-2 text-xs text-[color:var(--text-3)] flex-wrap">
                        <StatusBadge status={p.status} />
                        <span>{p.platform}</span>
                        {p.scheduled_at ? <span>· {new Date(p.scheduled_at).toLocaleString()}</span> : null}
                      </div>
                    </div>
                  </motion.li>
                ))}
              </ul>
            )}
          </GlassCard>

          {/* Recently Published — compact list, links to Content Manager */}
          <GlassCard className="p-5" delay={0.18} data-testid="dashboard-recently-published-card">
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="eyebrow">Recently</div>
                <div className="font-display text-xl font-semibold">Recently published</div>
              </div>
              <Link to="/content?tab=published" className="text-xs text-[color:var(--brand-primary)] hover:underline inline-flex items-center gap-1" data-testid="dashboard-recently-published-view-all">
                View all <ChevronRight className="h-3 w-3" />
              </Link>
            </div>
            {recentlyPublished.length === 0 ? (
              <div className="text-sm text-[color:var(--text-3)]" data-testid="dashboard-recently-published-empty">
                {hasZeroPosts
                  ? "Nothing has been published yet. Your first live post will show up here."
                  : "No published posts yet. Approve or publish a draft to see it here."}
              </div>
            ) : (
              <ul className="space-y-2">
                {recentlyPublished.map((p) => (
                  <motion.li
                    key={p.post_id}
                    initial={{ opacity: 0, y: 4 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="flex items-start gap-3 rounded-md border border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] p-2.5"
                    data-testid="dashboard-recently-published-row"
                  >
                    {p.image_url ? (
                      <img src={p.image_url} alt="" className="h-12 w-12 rounded-md object-cover border border-[color:var(--border-subtle)] shrink-0" />
                    ) : (
                      <div className="h-12 w-12 rounded-md bg-[color:var(--surface-3)] flex items-center justify-center text-[color:var(--brand-primary)] shrink-0">
                        <Send className="h-4 w-4" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-[color:var(--text-1)] line-clamp-2 whitespace-pre-line">{p.caption}</div>
                      <div className="mt-1 flex items-center gap-2 text-xs text-[color:var(--text-3)] flex-wrap">
                        <span className="inline-flex items-center gap-1 text-[color:var(--brand-primary)]">
                          <Send className="h-3 w-3" /> Published
                        </span>
                        <span>· {p.platform}</span>
                        {p.published_at ? <span>· {relTime(p.published_at)}</span> : null}
                      </div>
                    </div>
                  </motion.li>
                ))}
              </ul>
            )}
          </GlassCard>
        </div>

        {/* 9. Plan usage */}
        <div className="mb-4">
          <PlanUsageWidget />
        </div>

        {/* 10. Activity / weekly history — hide for brand new users
             with zero posts to avoid an empty-state pileup. */}
        {!hasZeroPosts ? (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 mb-4">
            <GlassCard className="p-5 lg:col-span-7" delay={0.1} data-testid="dashboard-weekly-card">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <div className="eyebrow">Weekly posting</div>
                  <div className="font-display text-xl font-semibold">Volume over time</div>
                </div>
                <div className="text-xs text-[color:var(--text-3)] flex items-center gap-3 flex-wrap">
                  <span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-[#1BA89F]" /> Published</span>
                  <span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-[#22D3EE]" /> Scheduled</span>
                  <span className="inline-flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-[color:var(--text-3)]" /> Drafts</span>
                </div>
              </div>
              <WeeklyChart data={weekly} />
              {/* Simplified footer: no AI share, no Plan-Usage duplication.
                  Best week hidden until there's meaningful history. */}
              <div className="hairline mt-3" />
              <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-3 text-xs">
                <div>
                  <div className="text-[color:var(--text-3)] uppercase tracking-wider text-[10px]">Total posts</div>
                  <div className="font-display text-lg font-semibold tabular-nums text-[color:var(--text-1)]" data-testid="dashboard-weekly-volume">
                    {totalPostsForWeeklyChart}
                  </div>
                  <div className="text-[10px] text-[color:var(--text-3)]">last 8 weeks</div>
                </div>
                <div>
                  <div className="text-[color:var(--text-3)] uppercase tracking-wider text-[10px]">Weekly average</div>
                  <div className="font-display text-lg font-semibold tabular-nums text-[color:var(--text-1)]" data-testid="dashboard-weekly-avg">
                    {weekly.length ? Math.round((weekly.reduce((s, w) => s + (w.published || 0) + (w.scheduled || 0), 0) / weekly.length) * 10) / 10 : 0}
                  </div>
                  <div className="text-[10px] text-[color:var(--text-3)]">posts / week</div>
                </div>
                {showBestWeek ? (
                  <div data-testid="dashboard-weekly-best">
                    <div className="text-[color:var(--text-3)] uppercase tracking-wider text-[10px]">Most active week</div>
                    <div className="font-display text-lg font-semibold tabular-nums text-[color:var(--text-1)]">
                      {(() => {
                        const best = weekly.reduce(
                          (acc, w) => (((w.published || 0) + (w.scheduled || 0)) > acc.v
                            ? { iso: w.iso_start, l: w.label, v: (w.published || 0) + (w.scheduled || 0) }
                            : acc),
                          { iso: null, l: "—", v: -1 },
                        );
                        if (best.v < 0) return "—";
                        const M = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
                        if (best.iso) {
                          const p = best.iso.split("-").map((n) => parseInt(n, 10));
                          if (p[0] && p[1] && p[2]) {
                            const s = new Date(p[0], p[1] - 1, p[2]);
                            const e = new Date(s); e.setDate(s.getDate() + 6);
                            const es = s.getMonth() === e.getMonth() ? `${e.getDate()}` : `${M[e.getMonth()]} ${e.getDate()}`;
                            return `${M[s.getMonth()]} ${s.getDate()} – ${es}`;
                          }
                        }
                        return best.l;
                      })()}
                    </div>
                    <div className="text-[10px] text-[color:var(--text-3)]">strongest activity</div>
                  </div>
                ) : null}
              </div>
            </GlassCard>

            <GlassCard className="p-5 lg:col-span-5" delay={0.12} data-testid="dashboard-activity-card">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <div className="eyebrow">Activity</div>
                  <div className="font-display text-xl font-semibold">What just happened</div>
                </div>
                <ActivityIcon className="h-4 w-4 text-[color:var(--text-3)]" />
              </div>
              <div className="max-h-[300px] overflow-auto pr-1">
                <ActivityFeed events={activity} emptyText="No activity yet — generate or run autopilot to start." />
              </div>
            </GlassCard>
          </div>
        ) : null}

        {/* 11. Engagement — honest-only. Widget already renders an
             explicit empty state until real analytics are available.
             Kept in the flow for existing users; suppressed at zero-post
             to avoid the empty-state pileup. */}
        {!hasZeroPosts ? (
          <div className="mb-4">
            <GlassCard className="p-5" delay={0.14} data-testid="dashboard-engagement-card">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <div className="eyebrow">Engagement</div>
                  <div className="font-display text-xl font-semibold">Post performance</div>
                </div>
              </div>
              <EngagementWidget data={engagement} />
            </GlassCard>
          </div>
        ) : null}

        {/* 12. Brand Learning status (dismissible) */}
        {showBrandLearning ? (
          <div className="mb-4">
            <BrandLearningStatusCard
              testid="dashboard-brand-learning-status"
              onDismiss={handleDismissBrandLearning}
            />
          </div>
        ) : null}

        {/* Compact business-profile summary — replaces the giant inline
             editor. Full editing moved to /settings/business-profile. */}
        <GlassCard className="p-5" delay={0.2} data-testid="dashboard-profile-summary-card">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="h-10 w-10 rounded-md bg-[color:rgba(27,168,159,0.12)] text-[color:var(--brand-primary)] flex items-center justify-center shrink-0">
              <Building2 className="h-4 w-4" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="eyebrow">Business profile</div>
              <div className="font-display text-base font-semibold text-[color:var(--text-1)] truncate" data-testid="dashboard-profile-summary-line">
                {profileSummary || "Your brand voice"}
              </div>
              {!profileComplete ? (
                <div className="text-xs text-[color:var(--warning)] mt-0.5">
                  Complete your profile so AI captions match your brand.
                </div>
              ) : (
                <div className="text-xs text-[color:var(--text-3)] mt-0.5">
                  Postmoro uses these details to write in your voice.
                </div>
              )}
            </div>
            <Link to="/settings/business-profile">
              <Button
                variant="outline"
                className="border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] text-[color:var(--text-1)]"
                data-testid="dashboard-edit-business-profile"
              >
                Edit business profile <ArrowRight className="h-4 w-4 ml-1" />
              </Button>
            </Link>
          </div>
        </GlassCard>
      </div>
    </div>
  );
}
